type CrashPayload = {
  message: string;
  stack?: string;
  source?: string;
  lineno?: number;
  colno?: number;
  timestamp: number;
  url?: string;
};

const recent: CrashPayload[] = [];
const MAX_RECENT = 20;

function record(payload: CrashPayload): void {
  recent.unshift(payload);
  if (recent.length > MAX_RECENT) recent.pop();
  console.error('[crash]', payload.message, payload.stack || '');
  void sendRemote(payload);
}

async function sendRemote(payload: CrashPayload): Promise<void> {
  try {
    const { MobileApiService } = await import('@/services/mobileApiService');
    const { Capacitor } = await import('@capacitor/core');
    const { MOBILE_APP_VERSION } = await import('@/mobile/constants/mobile');

    await MobileApiService.reportCrash({
      platform:
        Capacitor.getPlatform() === 'ios'
          ? 'ios'
          : Capacitor.getPlatform() === 'android'
            ? 'android'
            : 'web',
      appVersion: MOBILE_APP_VERSION,
      errorMessage: payload.message,
      stackTrace: payload.stack,
      occurredAt: new Date(payload.timestamp).toISOString(),
      meta: {
        source: payload.source,
        lineno: payload.lineno,
        colno: payload.colno,
        url: payload.url,
      },
    });
  } catch {
    // Swallow — crash reporting must never break the app
  }
}

export function getRecentCrashes(): CrashPayload[] {
  return [...recent];
}

export function initCrashReporting(): () => void {
  const onError = (event: ErrorEvent) => {
    record({
      message: event.message || 'Unhandled error',
      stack: event.error instanceof Error ? event.error.stack : undefined,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      timestamp: Date.now(),
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    record({
      message:
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'Unhandled promise rejection',
      stack: reason instanceof Error ? reason.stack : undefined,
      timestamp: Date.now(),
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
